import { marketing } from "./marketing.ts";

/**
 * Static document metadata for the web app's `index.html`.
 * Source of truth for the tokens stamped in by the Vite `transformIndexHtml`
 * plugin (`%APP_TITLE%`, `%APP_DESC%`, …) and the `<html lang>` attribute.
 */
export const meta = {
  lang: "en",
  title: "Music, Ads & Signage for Every Venue | OmniMedia",
  description:
    "The music, screens, announcements and promos of every venue you operate, run from one place. One flat price per device.",
  ogTitle: "OmniMedia",
  ogDescription:
    "The music, screens, announcements and promos of every venue you operate, run from one place. One flat price per device.",
  twitterTitle: "OmniMedia",
  twitterDescription:
    "The music, screens, announcements and promos of every venue you operate, run from one place. One flat price per device.",
} as const;

/** Canonical public origin. Also the base of the generated `sitemap.xml`. */
export const SITE_URL = "https://omnimedia.app";

/**
 * Per-page document metadata for the public marketing site.
 *
 * ONE source of truth with three consumers, so they cannot drift:
 *   1. `artifacts/web/src/marketing/routes.tsx` — the wouter public route table
 *   2. `artifacts/web/scripts/prerender.mjs` — static per-route HTML at build
 *   3. the same script's generated `sitemap.xml`
 *
 * Adding a page means adding a row here plus its component in `routes.tsx`;
 * a row without a component (or vice versa) fails the route-parity test in
 * `artifacts/web/src/marketing/routes.test.ts`.
 *
 * Copy traces to `marketing/web/landing-pages.md` (meta title/
 * description are character-budgeted there for Google's SERP limits).
 *
 * Kept OUTSIDE the `meta` catalog object because string-catalog leaves must be
 * strings/plural forms — this is structured data, not UI copy.
 */
export interface MarketingPageMeta {
  /** Route path, leading slash, no trailing slash (except the root "/"). */
  path: string;
  title: string;
  description: string;
  /** Public path to the social card; defaults to the site-wide opengraph.png. */
  ogImage?: string;
  changefreq: "weekly" | "monthly" | "yearly";
  /** Sitemap priority, "0.0".."1.0". */
  priority: string;
}

export const MARKETING_PAGES: readonly MarketingPageMeta[] = [
  {
    path: "/",
    title: meta.title,
    description: meta.description,
    changefreq: "weekly",
    priority: "1.0",
  },
  {
    path: "/features",
    title: "Music, Signage, Announcements & In-Store Ads | OmniMedia",
    description:
      "Four channels on one schedule: licensed music, screens, live announcements and promo spots. Flat €49.99 per device per month. 30-day free trial.",
    changefreq: "monthly",
    priority: "0.8",
  },
  {
    path: "/apps",
    title: "Web, Desktop, Mobile & TV Players | OmniMedia",
    description:
      "One platform, every screen: the browser player, the offline desktop app, the Android TV box and the mobile companion. See each one running.",
    changefreq: "monthly",
    priority: "0.8",
  },
  // NB: there is no `/downloads` page. Where to get each player is a section
  // on the landing page (`/#downloads`), so it carries the homepage's
  // metadata rather than its own.
  {
    path: "/compare",
    title: "OmniMedia vs Music-Only and Signage-Only Tools",
    description:
      "How OmniMedia compares with music-only and signage-only tools, and how it fits restaurants, retail, gyms and hotels. €49.99 per device per month.",
    changefreq: "monthly",
    priority: "0.6",
  },
  // NB: `/support` is deliberately absent. It is the authenticated in-app
  // ticket surface (routed through AuthenticatedApp), so listing it here would
  // hand crawlers a page that redirects to sign-in. The old hand-maintained
  // sitemap.xml did exactly that.
  {
    path: "/privacy",
    title: "Privacy Policy | OmniMedia",
    description:
      "How TrueSmartTech OÜ collects, uses and protects personal data in OmniMedia.",
    changefreq: "yearly",
    priority: "0.3",
  },
  {
    path: "/terms",
    title: "Terms of Service | OmniMedia",
    description:
      "The terms governing use of OmniMedia, operated by TrueSmartTech OÜ, Tallinn, Estonia.",
    changefreq: "yearly",
    priority: "0.3",
  },
  {
    path: "/cookies",
    title: "Cookie Policy | OmniMedia",
    description:
      "Which cookies OmniMedia sets, what they do, and how to control them.",
    changefreq: "yearly",
    priority: "0.3",
  },
  // The public deletion URL declared to Google Play (Data safety) and App
  // Store Connect. Prerendered like the other legal pages so a reviewer
  // opening it gets its own title and card, not the homepage's.
  {
    path: "/delete-account",
    title: "Delete Your Account | OmniMedia",
    description:
      "How to delete your OmniMedia account and data: in the app, on the web, or by email. What is deleted, and what is kept.",
    changefreq: "yearly",
    priority: "0.3",
  },
] as const;

/**
 * Head metadata for the public not-found page. Deliberately NOT a row of
 * `MARKETING_PAGES`: it must never reach the sitemap or the route-parity test,
 * and scripts/prerender.mjs writes it to `404.html` rather than
 * `<route>/index.html`. Copy comes from the same catalog the page renders.
 */
export const NOT_FOUND_PAGE = {
  title: `${marketing.notFound.title} | ${meta.ogTitle}`,
  description: marketing.notFound.body,
} as const;

/**
 * The brand's own social profiles, in the order the footer renders them.
 *
 * ONE source of truth with two consumers: the footer's icon row
 * (`components/landing/social-links.tsx`) and the Organization JSON-LD's
 * `sameAs`, which is how a search engine is told these accounts belong to this
 * brand. A profile only belongs here once it is live and posting: every URL was
 * fetched and confirmed to serve the OmniMedia account before being added
 * (2026-09-06), and `marketing/social/PUBLISHED.md` records the posts.
 *
 * TikTok is deliberately absent: the OmniMedia account is not connected yet.
 *
 * Kept OUTSIDE the `meta` catalog object because string-catalog leaves must be
 * strings/plural forms — this is structured data, not UI copy. The visible
 * label for each row lives in `marketing.footer.social.*`.
 */
export const SOCIAL_PROFILES = [
  { id: "linkedin", url: "https://www.linkedin.com/company/omnimediaapp/" },
  { id: "instagram", url: "https://www.instagram.com/omnimedia.app/" },
  { id: "x", url: "https://x.com/omnimediaapp" },
  { id: "facebook", url: "https://www.facebook.com/122102383749463047" },
] as const;

/** The id of a social profile row, used to resolve its icon and label. */
export type SocialProfileId = (typeof SOCIAL_PROFILES)[number]["id"];

/**
 * JSON-LD structured data stamped into index.html's
 * `<script type="application/ld+json">%APP_JSONLD%</script>` by the same Vite
 * `transformIndexHtml` plugin that stamps the tokens above. Kept OUTSIDE the
 * `meta` catalog object because string-catalog leaves must be strings/plural
 * forms — this is data, not UI copy. Names/descriptions derive from `meta` so
 * there is a single source of truth for titles.
 */
export const jsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: meta.ogTitle,
    legalName: "TrueSmartTech OÜ",
    url: SITE_URL,
    logo: `${SITE_URL}/brand/png/icon-rounded-192.png`,
    email: "info@truesmarttech.com",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Tallinn",
      addressCountry: "EE",
    },
    // Claims the brand's social accounts as the same entity as this site.
    sameAs: SOCIAL_PROFILES.map((p) => p.url),
  },
  {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: meta.ogTitle,
    description: meta.description,
    url: SITE_URL,
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    offers: [
      {
        "@type": "Offer",
        price: "49.99",
        priceCurrency: "EUR",
        description: "Per device per month, everything included.",
      },
      {
        "@type": "Offer",
        price: "499.99",
        priceCurrency: "EUR",
        description:
          "Per device per year, about two months free, everything included.",
      },
    ],
  },
] as const;
